import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { ROLES, type ActorDTO, type AuthMode, type Role, type Runtime } from "@roomate/core";
import { keys, useActors, useCreate, useMe, useNodes, usePost } from "../api.ts";
import { defaultRuntime, SEAT_RUNTIMES, seatStatus } from "../lib/seats.ts";
import { RUNTIME_LABEL } from "../lib/run-runtime.ts";
import { RemoveMemberButton, RestoreMemberButton } from "../components/MemberArchive.tsx";
import { NodeServices } from "../components/team-nodes/NodeServices.tsx";
import { removedMembers, teamPeople } from "../roster.ts";
import { ActorName, Ago, authModeOf, Button, cx, ErrorNote, Glyph, inputCls } from "../ui.tsx";
import { Avatar, Bento, Block, Disclosure, Page, PageHeader, Pill } from "../calm.tsx";
import { Dialog, Field } from "./Project.tsx";
import { AgentConfigButton } from "./AgentConfig.tsx";
import { SandboxPanel } from "./SandboxPanel.tsx";

export function emptySkillsLabel(actor: Pick<ActorDTO, "kind" | "canBuild">): string {
  return actor.kind === "human" && actor.canBuild === false
    ? "review only: never routed as a builder"
    : "no skills set: routing treats every task as a stretch";
}

export function TeamPage() {
  const actors = useActors().data ?? [];
  const me = useMe().data!;
  // Removed people stay in the actor list so history can name them, and out of the room.
  const humans = teamPeople(actors);
  const removed = me.access === "admin" ? removedMembers(actors) : [];
  const agents = actors.filter((a) => a.kind === "agent");
  return (
    <Page wide>
      <PageHeader
        title="The room"
        meta={<span className="text-ink-2">Skills and focus drive routing; no personal scores, no leaderboard.</span>}
        actions={me.access === "admin" && <AddActor humans={humans} />}
      />
      <div className="mt-8 space-y-8">
        <Block title={`People · ${humans.length}`}>
          <Bento>
            {humans.map((a) => (
              <ActorCard key={a.id} actor={a} actors={actors} me={me} />
            ))}
          </Bento>
        </Block>
        <Block title={`Agents · ${agents.length}`} aside="connect through MCP: on the owner's seat, or with a shared API key">
          <Bento>
            {agents.map((a) => (
              <ActorCard key={a.id} actor={a} actors={actors} me={me} />
            ))}
          </Bento>
        </Block>
        <NodeServices />
        {removed.length > 0 && <RemovedMembers actors={removed} />}
      </div>
    </Page>
  );
}

function RemovedMembers({ actors }: { actors: ActorDTO[] }) {
  const [open, setOpen] = useState(false);
  return (
    <Block title="Removed">
      <button type="button" className="text-[13px] text-ink-2 hover:text-ink" data-testid="removed-members" onClick={() => setOpen((value) => !value)}>
        {open ? "Hide" : "Show"} {actors.length} removed {actors.length === 1 ? "person" : "people"}
      </button>
      {open && (
        <ul className="mt-3 space-y-2">
          {actors.map((actor) => (
            <li key={actor.id} className="flex flex-wrap items-center justify-between gap-2 text-[14px]">
              <span>{actor.name}</span>
              <RestoreMemberButton actor={actor} />
            </li>
          ))}
        </ul>
      )}
    </Block>
  );
}

function ActorCard({ actor, actors, me }: { actor: ActorDTO; actors: ActorDTO[]; me: ActorDTO }) {
  const canToken = me.access === "admin" || actor.ownerId === me.id || actor.id === me.id;
  // Agents always carry a run-capacity line (maxParallel), so their Details disclosure should
  // always be offered, not just when focus/areas/lastSeenAt happen to be set.
  const hasDetails = actor.kind === "agent" || actor.canBuild === false || actor.focus.length > 0 || actor.allowedAreas.length > 0 || !!actor.lastSeenAt;
  return (
    <article className={cx("relative min-w-0 rounded-[22px] md:col-span-2", !actor.active && "opacity-50")}>
      {/* A separate, non-filtered, non-animated layer: `.glass`'s backdrop-filter, and a
          transform/opacity entrance animation like Tile's `os-rise`, both make their element a
          containing block for `position: fixed` descendants. The Connect/Run-config dialogs
          are rendered as plain descendants here (no portal), so putting either on the card
          itself traps their overlay inside the card's box instead of the viewport — confirmed
          via `pnpm --filter @roomate/e2e test --grep "connects an agent"` (Playwright reported
          this card's own content intercepting clicks on the dialog). `-z-10` (not z-index on
          the content below) keeps the content unpositioned so it can't trap the dialog either. */}
      <div className="glass absolute inset-0 -z-10 rounded-[22px]" aria-hidden />
      <div className="p-5 md:p-6">
        {/* Wraps so the actions drop below the name on narrow cards instead of squeezing the name to nothing. */}
        <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-3">
          <div className="flex min-w-[10rem] flex-1 items-center gap-3">
            <Avatar name={actor.name} kind={actor.kind} size={44} live={actor.online} />
            <div className="min-w-0">
              <h3 className="truncate text-[16px] font-medium leading-snug">
                <Link to={`/team/${actor.id}`} className="hover:underline">
                  {actor.name}
                </Link>
              </h3>
              <p className="truncate text-[12.5px] text-ink-3">
                {actor.kind === "agent" ? (
                  <>
                    {actor.runtime} · <ActorName id={actor.ownerId} actors={actors} />
                  </>
                ) : (
                  <>
                    {actor.role.replace("_", " ")} · {actor.access}
                  </>
                )}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {actor.kind === "agent" && (
              <span
                data-testid="auth-mode"
                data-auth={authModeOf(actor)}
                title={authModeOf(actor) === "api_key" ? "calls the Anthropic API with this node's own key" : "runs on the owner's Claude Code seat"}
              >
                <Pill tone={authModeOf(actor) === "api_key" ? "signal" : "neutral"}>{authModeOf(actor) === "api_key" ? "api key" : "seat"}</Pill>
              </span>
            )}
            {actor.kind === "agent" && canToken && <AgentConfigButton actor={actor} />}
            {canToken && <TokenButton actor={actor} />}
            {me.access === "admin" && actor.kind === "human" && <RemoveMemberButton actor={actor} />}
          </div>
        </div>

        <div className="mt-3.5 flex flex-wrap gap-1.5">
          {actor.skills.map((s) => (
            <span key={s.key} role="img" aria-label={`${s.key} level ${s.level} of 5`}>
              <Pill>
                {s.key} · {s.level}
              </Pill>
            </span>
          ))}
          {actor.skills.length === 0 && (
            <p className="text-[12.5px] italic text-ink-3">
              {emptySkillsLabel(actor)}
            </p>
          )}
        </div>

        <SandboxPanel actor={actor} admin={me.access === "admin"} />

        {hasDetails && (
          <Disclosure summary="Details" className="mt-3.5">
            <div className="space-y-1 text-[12.5px] text-ink-2">
              {actor.focus.length > 0 && <p>focus: {actor.focus.join(", ")}</p>}
              {actor.kind === "human" && actor.canBuild === false && <p>review only, never builds</p>}
              {actor.kind === "agent" && <p>{actor.maxParallel === 0 ? "review only, never builds" : `up to ${actor.maxParallel} task${actor.maxParallel === 1 ? "" : "s"} at once`}</p>}
              {actor.allowedAreas.length > 0 && <p className="font-mono text-[11.5px]">only {actor.allowedAreas.join(", ")}</p>}
              {actor.lastSeenAt && (
                <p>
                  seen <Ago ts={actor.lastSeenAt} />
                  {actor.activity && `: ${actor.activity}`}
                </p>
              )}
            </div>
          </Disclosure>
        )}
      </div>
    </article>
  );
}

function TokenButton({ actor }: { actor: ActorDTO }) {
  const create = usePost<{ token: string }>([keys.actors]);
  const [token, setToken] = useState<string | null>(null);
  return (
    <>
      <Button variant="ghost" className="px-2 text-[12px]" onClick={() => create.mutate({ path: `/actors/${actor.id}/tokens`, body: { label: "web" } }, { onSuccess: (r) => setToken(r.token) })}>
        {actor.kind === "agent" ? "Connect" : "Token"}
      </Button>
      {token && (
        <Dialog title={actor.kind === "agent" ? `Connect ${actor.name}` : `Token for ${actor.name}`} onClose={() => setToken(null)}>
          <ConnectInfo token={token} agent={actor.kind === "agent"} runtime={actor.runtime} actorId={actor.id} authMode={authModeOf(actor)} />
        </Dialog>
      )}
      <ErrorNote error={create.error} />
    </>
  );
}

function ConnectInfo({ token, agent, runtime, actorId, authMode }: { token: string; agent: boolean; runtime: Runtime | null; actorId: string; authMode: AuthMode }) {
  const url = `${location.origin}/mcp`;
  const snippets: Record<string, string> = {
    "claude-code": `claude mcp add --transport http roomate ${url} --header 'Authorization: Bearer \${ROOMATE_TOKEN}'`,
    cursor: JSON.stringify({ mcpServers: { roomate: { url, headers: { Authorization: "Bearer ${env:ROOMATE_TOKEN}" } } } }, null, 2),
    vscode: JSON.stringify(
      {
        inputs: [{ type: "promptString", id: "roomate-token", description: "Roomate agent token", password: true }],
        servers: { roomate: { type: "http", url, headers: { Authorization: "Bearer ${input:roomate-token}" } } },
      },
      null,
      2,
    ),
    codex: `[mcp_servers.roomate]\nurl = "${url}"\nbearer_token_env_var = "ROOMATE_TOKEN"`,
  };
  const files: Record<string, string> = { "claude-code": "terminal", cursor: ".cursor/mcp.json", vscode: ".vscode/mcp.json", codex: "~/.codex/config.toml" };
  const [tab, setTab] = useState<string>(runtime && runtime in snippets ? runtime : "claude-code");
  return (
    <div className="space-y-3 text-[13px]">
      <p>This token is shown once. Keep it in an environment variable, never in a file you commit.</p>
      <pre className="overflow-x-auto rounded-md bg-ink p-3 font-mono text-[12px] text-paper" data-testid="new-token">
        export ROOMATE_TOKEN={token}
      </pre>
      {agent && (
        <>
          <div className="flex gap-1">
            {Object.keys(snippets).map((k) => (
              <button key={k} onClick={() => setTab(k)} className={cx("rounded px-2 py-1 font-mono text-[11.5px]", tab === k ? "bg-ink text-paper" : "text-ink-2 hover:bg-paper-2")}>
                {k}
              </button>
            ))}
          </div>
          <p className="font-mono text-[11px] text-ink-3">{files[tab]}</p>
          <pre className="overflow-x-auto rounded-md border border-rule bg-paper p-3 font-mono text-[12px]">{snippets[tab]}</pre>
          {tab === "cursor" && <p className="text-[12px] text-ink-3">Cursor reads its own environment: on macOS, start it from a shell that exports ROOMATE_TOKEN.</p>}
          {tab === "vscode" && <p className="text-[12px] text-ink-3">VS Code asks for the token once and keeps it in its secret storage.</p>}
          {tab === "claude-code" && <p className="text-[12px] text-ink-3">Single quotes matter: Claude Code then stores the variable reference, not the token.</p>}
          <p className="text-[12px] text-ink-3">Or let your laptop run it for you</p>
          <pre className="overflow-x-auto rounded-md border border-rule bg-paper p-3 font-mono text-[12px]">{`roomate node agent ${actorId} ${token}\nroomate node`}</pre>
          <p className="text-ink-2">
            {authMode === "api_key"
              ? "This agent calls the Anthropic API directly with this node's own ANTHROPIC_API_KEY, not a personal seat."
              : "This agent runs on its owner's own Claude Code seat."}{" "}
            Roomate never calls a model. Add the protocol to the repo with <code className="font-mono">roomate agents-md</code>.
          </p>
        </>
      )}
    </div>
  );
}

function AddActor({ humans }: { humans: ActorDTO[] }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<"human" | "agent">("human");
  const [name, setName] = useState("");
  const [role, setRole] = useState<Role>("backend");
  const nodes = useNodes().data ?? [];
  const meId = useMe().data?.id ?? "";
  // Default to the first ready runtime on the creator's node (claude-code if no node reported); a pick overrides it.
  const [pickedRuntime, setRuntime] = useState<Runtime | null>(null);
  const [ownerId, setOwnerId] = useState("");
  const runtime: Runtime = pickedRuntime ?? defaultRuntime(nodes, ownerId || meId);
  const [skills, setSkills] = useState("backend:4");
  const [focus, setFocus] = useState("");
  const [created, setCreated] = useState<ActorDTO | null>(null);
  const create = useCreate<ActorDTO>("/actors", [keys.actors]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = skills
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => {
        const [key, level] = s.split(":");
        return { key: key!.trim(), level: Number(level ?? 3) };
      });
    create.mutate(
      {
        kind,
        name,
        skills: parsed,
        ...(kind === "human" ? { role, focus: focus.split(",").map((f) => f.trim()).filter(Boolean) } : { runtime, ownerId }),
      },
      { onSuccess: (a) => setCreated(a) },
    );
  };
  return (
    <>
      <Button variant="primary" onClick={() => setOpen(true)}>
        + Add teammate or agent
      </Button>
      {open && (
        <Dialog title={created ? `${created.name} added` : "Add to the room"} onClose={() => (setOpen(false), setCreated(null))}>
          {created ? (
            <div className="space-y-3 text-[13px]">
              <p>Next, issue a token from their card ({created.kind === "agent" ? "Connect" : "Token"}).</p>
              <Button onClick={() => (setOpen(false), setCreated(null))}>Done</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div className="flex gap-1">
                {(["human", "agent"] as const).map((k) => (
                  <button key={k} type="button" onClick={() => setKind(k)} className={cx("rounded px-3 py-1 text-[13px]", kind === k ? "bg-ink text-paper" : "border border-rule")}>
                    <Glyph kind={k} /> {k}
                  </button>
                ))}
              </div>
              <Field label="Name">
                <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
              </Field>
              {kind === "human" ? (
                <>
                  <Field label="Role">
                    <select className={inputCls} value={role} onChange={(e) => setRole(e.target.value as Role)}>
                      {ROLES.map((r) => (
                        <option key={r}>{r}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Focus" hint="comma-separated, e.g. security, compliance, gtm">
                    <input className={inputCls} value={focus} onChange={(e) => setFocus(e.target.value)} />
                  </Field>
                </>
              ) : (
                <>
                  <Field label="Runtime">
                    <select className={inputCls} value={runtime} onChange={(e) => setRuntime(e.target.value as Runtime)}>
                      {SEAT_RUNTIMES.map((r) => {
                        const ready = nodes.some((n) => n.memberId === (ownerId || meId) && seatStatus(n, r)?.ready);
                        return <option key={r} value={r}>{ready ? `${RUNTIME_LABEL[r]} (ready)` : RUNTIME_LABEL[r]}</option>;
                      })}
                    </select>
                  </Field>
                  <Field label="Owner" hint="gets its questions; whose seat it runs on by default (change in Run config)">
                    <select className={inputCls} value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
                      <option value="">No owner</option>
                      {humans.map((h) => (
                        <option key={h.id} value={h.id}>
                          {h.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                </>
              )}
              <Field label="Skills" hint="key:level 1-5, comma-separated">
                <input className={cx(inputCls, "font-mono")} value={skills} onChange={(e) => setSkills(e.target.value)} />
              </Field>
              <ErrorNote error={create.error} />
              <div className="flex justify-end">
                <Button variant="primary" type="submit" disabled={!name.trim() || (kind === "agent" && !ownerId) || create.isPending}>
                  Add
                </Button>
              </div>
            </form>
          )}
        </Dialog>
      )}
    </>
  );
}
